import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { Listbox } from '@headlessui/react';
import { ChevronUpDownIcon, XMarkIcon } from '@heroicons/react/20/solid';

import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Heading from '@tiptap/extension-heading';
import TurndownService from 'turndown';

import CoverImageUploader from '@/components/blog/CoverImageUploader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';

import { toast } from '@/hooks/use-toast';
import { handleServerError } from '@/utils/handle-server-error';
import {
  list_blog_categories,
  create_blog_category,
  deleteBlogCategory,
  list_blog_tags,
  create_blog_tag,
  get_superadmin_blog_post,
  update_superadmin_blog_post,
  BlogCategory,
  BlogTag,
  BlogPost,
  BlogDomain,
  list_blog_domains,
} from '@/api/blog';

interface SearchParams {
  id?: string;
}

export default function BlogEdit() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useSearch({ from: '/_authenticated/super-admin-blog-edit' }) as SearchParams;

  /* =========================
     Domains
  ========================= */
  const [domains, setDomains] = useState<BlogDomain[]>([]);
  const [domainName, setDomainName] = useState<string>('');

  /* =========================
     Draft
  ========================= */
  const [blog, setBlog] = useState<BlogPost | null>(null);

  /* =========================
     Fields
  ========================= */
  const [title, setTitle] = useState('');
  const [summary, setSummary] = useState('');
  const [content, setContent] = useState('');
  const [isRTL, setIsRTL] = useState(false);
  const [saving, setSaving] = useState(false);

  /* =========================
     Categories
  ========================= */
  const [categories, setCategories] = useState<BlogCategory[]>([]);
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [newCategory, setNewCategory] = useState('');
  const [creatingCategory, setCreatingCategory] = useState(false);

  /* =========================
     Tags
  ========================= */
  const [tags, setTags] = useState<BlogTag[]>([]);
  const [selectedTags, setSelectedTags] = useState<BlogTag[]>([]);
  const [newTag, setNewTag] = useState('');
  const [creatingTag, setCreatingTag] = useState(false);

  /* =========================
     Cover Ref
  ========================= */
  interface CoverUploaderRef {
    uploadImage: (id?: string) => Promise<string | null>;
    uploading: boolean;
  }

  const coverRef = useRef<CoverUploaderRef>(null);

  /* =========================
     Editor
  ========================= */
  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Heading.configure({ levels: [1, 2, 3] }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
    ],
    editorProps: {
      attributes: {
        class:
          'min-h-[400px] p-4 border rounded-md bg-white focus:outline-none prose max-w-none',
        dir: isRTL ? 'rtl' : 'ltr',
      },
      handleTextInput(view, _from, _to, text) {
        const currentLength = view.state.doc.textContent.length;
        return currentLength + text.length > CONTENT_MAX;
      },
      handlePaste(view, event) {
        const pasteText = event.clipboardData?.getData('text/plain') ?? '';
        const currentLength = view.state.doc.textContent.length;
        return currentLength + pasteText.length > CONTENT_MAX;
      },
    },
    onUpdate: ({ editor }) => {
      setContent(editor.getHTML());
    },
  });


  /* =========================
     Load Data
  ========================= */
  useEffect(() => {
    if (!id || !editor) return;

    const load = async () => {
      try {
        const [doms, draft] = await Promise.all([
          list_blog_domains(),
          get_superadmin_blog_post(id),
        ]);

        setDomains(doms);
        setBlog(draft);

        setTitle(draft.title);
        setSummary(draft.summary || '');
        setDomainName(draft.domain_name || '');

        // Fetch categories and tags based on draft's domain (if any)
        const [cats, tagsList] = await Promise.all([
          list_blog_categories(draft.domain_name),
          list_blog_tags(draft.domain_name),
        ]);

        setCategories(cats);
        setTags(tagsList);

        setCategoryId(draft.category?.id || null);
        setSelectedTags(draft.hashtags || []);
        setIsRTL(draft.language === 'ar');

        editor.commands.setContent(draft.content_markdown || '');
      } catch (error) {
        handleServerError(error);
      }
    };

    load();
  }, [id, editor]);

  // Handle domain change
  useEffect(() => {
    if (!blog) return; // Prevent initial double-fetch

    const updateCatsAndTags = async () => {
      try {
        // Only reset selections if the user manually changes the domain from what was loaded
        if (domainName !== blog.domain_name) {
          setCategoryId(null);
          setSelectedTags([]);
        }
        const [cats, tagsList] = await Promise.all([
          list_blog_categories(domainName),
          list_blog_tags(domainName),
        ]);
        setCategories(cats);
        setTags(tagsList);
      } catch (e) {
        handleServerError(e);
      }
    };

    updateCatsAndTags();
  }, [domainName, blog?.domain_name]); // Dependency slightly loose to allow the initial load, rely on the if checks

  /* =========================
     Category CRUD
  ========================= */
  const handleCreateCategory = async () => {
    if (!newCategory.trim()) return;
    if (!domainName) {
      toast({ title: t('error'), description: t('select_domain_first', 'Please select a domain first'), variant: 'destructive' });
      return;
    }
    try {
      setCreatingCategory(true);
      const created = await create_blog_category(newCategory.trim(), domainName);
      setCategories(await list_blog_categories(domainName));
      setCategoryId(created.id);
      setNewCategory('');
    } catch (e) {
      handleServerError(e);
    } finally {
      setCreatingCategory(false);
    }
  };

  const handleDeleteCategory = async (id: string) => {
    if (!confirm('Delete category?')) return;
    try {
      await deleteBlogCategory(id);
      if (categoryId === id) setCategoryId(null);
      setCategories(await list_blog_categories(domainName));
    } catch (e) {
      handleServerError(e);
    }
  };

  /* =========================
     Tag CRUD
  ========================= */
  const handleCreateTag = async () => {
    if (!newTag.trim()) return;
    if (!domainName) {
      toast({ title: t('error'), description: t('select_domain_first', 'Please select a domain first'), variant: 'destructive' });
      return;
    }
    try {
      setCreatingTag(true);
      const created = await create_blog_tag(newTag.trim(), domainName);
      setTags(await list_blog_tags(domainName));
      setSelectedTags([...selectedTags, created]);
      setNewTag('');
    } catch (e) {
      handleServerError(e);
    } finally {
      setCreatingTag(false);
    }
  };

  /* =========================
     Save
  ========================= */
  const handleSave = async () => {
    if (!blog || !editor) return;
    if (!domainName) {
      toast({ title: t('error'), description: t('domain_required', 'Domain is required'), variant: 'destructive' });
      return;
    }

    try {
      setSaving(true);

      let coverUrl: string | null = null;
      if (coverRef.current) {
        coverUrl = await coverRef.current.uploadImage(blog.id);
      }

      // Convert HTML to Markdown
      const turndownService = new TurndownService();
      const markdownContent = turndownService.turndown(content);

      await update_superadmin_blog_post(blog.id, {
        title,
        summary,
        content_markdown: markdownContent,
        category_id: categoryId,
        hashtag_ids: selectedTags.map(t => t.id),
        language: isRTL ? 'ar' : 'fr',
        domain_name: domainName,
        ...(coverUrl ? { cover_image: coverUrl } : {}),
      });

      navigate({ to: '/super-admin-blog' });
    } catch (error) {
      handleServerError(error);
    } finally {
      setSaving(false);
    }
  };

  /* =========================
     Render
  ========================= */
  const TITLE_RECOMMENDED_MAX = 60;
  const SUMMARY_RECOMMENDED_MAX = 160;
  const CONTENT_MAX = 50000; // Increased limit for long articles

  return (
    <div className="p-6 space-y-6 max-w-5xl mx-auto">
      <CoverImageUploader
        ref={coverRef}
        blogId={blog?.id || ''}
        initialImage={blog?.cover_image} // Pass existing image
      />

      <Card>
        <CardHeader>
          <CardTitle>{t('edit_blog')}</CardTitle>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Domain */}
          <div className="space-y-2">
            <label className="text-sm font-medium">{t('blog_domain', 'Domain')} <span className="text-red-500">*</span></label>
            <Listbox value={domainName} onChange={setDomainName}>
              <div className="relative mt-1">
                <Listbox.Button className="relative w-full md:w-72 cursor-pointer rounded border bg-white py-2 pl-3 pr-10 text-left">
                  {domainName || t('select_domain', 'Select a domain')}
                  <ChevronUpDownIcon className="h-5 w-5 absolute right-2 top-2.5 text-gray-400" />
                </Listbox.Button>
                <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full bg-white border rounded shadow overflow-auto">
                  {domains.map((dom) => (
                    <Listbox.Option
                      key={dom.id}
                      value={dom.name}
                      className={({ active }) => `cursor-pointer select-none relative py-2 pl-3 pr-9 ${active ? 'bg-indigo-600 text-white' : 'text-gray-900'}`}
                    >
                      {dom.name}
                    </Listbox.Option>
                  ))}
                </Listbox.Options>
              </div>
            </Listbox>
            <p className="text-xs text-muted-foreground">{t('domain_selection_hint', 'Categories and Tags are specific to the selected domain.')}</p>
          </div>

          {/* Title */}
          <div className="space-y-1 border-t pt-6">
            <div className="flex justify-between">
              <label className="text-sm font-medium">{t('blog_title')}</label>
              <span className={`text-xs ${title.length > TITLE_RECOMMENDED_MAX ? 'text-orange-500' : 'text-gray-500'}`}>
                {title.length} / {TITLE_RECOMMENDED_MAX} (SEO)
              </span>
            </div>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t('blog_title')}
            />
            <p className="text-xs text-muted-foreground mt-1">
              {t('seo_title_recommendation', 'Recommended: 50-60 characters for best SEO results.')}
            </p>
          </div>

          {/* Summary */}
          <div className="space-y-1">
            <div className="flex justify-between">
              <label className="text-sm font-medium">{t('blog_summary')}</label>
              <span className={`text-xs ${summary.length > SUMMARY_RECOMMENDED_MAX ? 'text-orange-500' : 'text-gray-500'}`}>
                {summary.length} / {SUMMARY_RECOMMENDED_MAX} (SEO)
              </span>
            </div>
            <Input
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              placeholder={t('blog_summary')}
            />
            <p className="text-xs text-muted-foreground mt-1">
              {t('seo_summary_recommendation', 'Recommended: 150-160 characters for best SEO results.')}
            </p>
          </div>
          {/* Content */}
          <div className="space-y-1">
            <label className="text-sm font-medium">{t('content')}</label>
            {/* Toolbar */}
            <div className="flex flex-wrap gap-2 border rounded-md p-2 bg-gray-50">
              <Button size="sm" variant="outline" onClick={() => editor?.chain().focus().toggleBold().run()}>Bold</Button>
              <Button size="sm" variant="outline" onClick={() => editor?.chain().focus().toggleItalic().run()}>Italic</Button>
              <Button size="sm" variant="outline" onClick={() => editor?.chain().focus().toggleUnderline().run()}>Underline</Button>
              <Button size="sm" variant="outline" onClick={() => editor?.chain().focus().toggleHeading({ level: 1 }).run()}>H1</Button>
              <Button size="sm" variant="outline" onClick={() => editor?.chain().focus().toggleHeading({ level: 2 }).run()}>H2</Button>
              <Button size="sm" variant="outline" onClick={() => editor?.chain().focus().toggleBulletList().run()}>• List</Button>
              <Button size="sm" variant="outline" onClick={() => editor?.chain().focus().toggleOrderedList().run()}>1. List</Button>
            </div>
            <EditorContent editor={editor} />
          </div>

          <p
            className={`text-sm text-right ${editor?.getText().length >= CONTENT_MAX
              ? 'text-red-500'
              : 'text-gray-500'
              }`}
          >
            {editor?.getText().length || 0} / {CONTENT_MAX}
          </p>
          {/* Language */}
          <div className="flex items-center gap-3">
            <Switch checked={isRTL} onCheckedChange={setIsRTL} />
            <span>{isRTL ? t('Arabe') : t('Français')}</span>
          </div>
          {/* Category */}
          <div className="space-y-1">
            <label className="text-sm font-medium">{t('category')}</label>
            <Listbox value={categoryId} onChange={setCategoryId}>
              <div className="relative mt-1">
                <Listbox.Button className="relative w-72 cursor-pointer rounded border bg-white py-2 pl-3 pr-10 text-left">
                  {categoryId
                    ? categories.find(c => c.id === categoryId)?.name
                    : t('select_category')}
                  <ChevronUpDownIcon className="h-5 w-5 absolute right-2 top-2.5" />
                </Listbox.Button>
                <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full bg-white border rounded shadow">
                  {categories.map(cat => (
                    <Listbox.Option
                      key={cat.id}
                      value={cat.id}
                      className="flex justify-between items-center p-2 cursor-pointer hover:bg-gray-100"
                    >
                      {cat.name}
                      <button onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteCategory(cat.id);
                      }}>
                        <XMarkIcon className="w-4 h-4 text-red-500" />
                      </button>
                    </Listbox.Option>
                  ))}
                </Listbox.Options>
              </div>
            </Listbox>
          </div>


          {/* New Category */}
          <div className="space-y-1">
            <label className="text-sm font-medium">{t('new_category')}</label>
            <div className="flex gap-2">
              <Input
                value={newCategory}
                onChange={(e) => setNewCategory(e.target.value)}
                placeholder={t('new_category')}
              />
              <Button onClick={handleCreateCategory} disabled={creatingCategory}>
                {t('create_b')}
              </Button>
            </div>
          </div>

          {/* Tags */}
          <div className="space-y-1">
            <label className="text-sm font-medium">{t('tags')}</label>
            <div className="flex flex-wrap gap-2">
              {tags.map(tag => {
                const selected = selectedTags.some(t => t.id === tag.id);
                return (
                  <button
                    key={tag.id}
                    onClick={() =>
                      selected
                        ? setSelectedTags(selectedTags.filter(t => t.id !== tag.id))
                        : setSelectedTags([...selectedTags, tag])
                    }
                    className={`px-2 py-1 rounded ${selected ? 'bg-indigo-500 text-white' : 'bg-gray-200'
                      }`}
                  >
                    {tag.name}
                  </button>
                );
              })}
            </div>
          </div>

          {/* New Tag */}
          <div className="space-y-1">
            <label className="text-sm font-medium">{t('new_tag')}</label>
            <div className="flex gap-2">
              <Input
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                placeholder={t('new_tag')}
              />
              <Button onClick={handleCreateTag} disabled={creatingTag}>
                {t('create_t')}
              </Button>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => navigate({ to: '/super-admin-blog' })}>
              {t('cancel')}
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? t('saving') : t('save')}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}